import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import TrueFalseExercise from './TrueFalseExercise';
import './TrueFalseEmbed.css';
import { useExerciseSync } from '../../context/ExerciseSyncContext';

const TRUE_FALSE_ICON = (
  <span className="default-icon-spacing" style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)'
  }}>
    <svg width="28" height="28" viewBox="0 0 34 34" fill="white">
      <g>
        <g>
          <path d="m30 15h-6v1c0 2.8-2.2 5-5 5h-7v8c0 1.7 1.3 3 3 3h15c1.7 0 3-1.3 3-3v-11c0-1.7-1.3-3-3-3zm-2.3 6.7-6 6c-.2.2-.4.3-.7.3s-.5-.1-.7-.3l-3-3c-.4-.4-.4-1 0-1.4s1-.4 1.4 0l2.3 2.3 5.3-5.3c.4-.4 1-.4 1.4 0s.4 1 0 1.4z"/>
        </g>
        <g>
          <path d="m19 2h-15c-1.7 0-3 1.3-3 3v11c0 1.7 1.3 3 3 3h15c1.7 0 3-1.3 3-3v-11c0-1.7-1.3-3-3-3zm-3.4 11.2c.4.4.4 1 0 1.4s-1 .4-1.4 0l-2.7-2.7-2.7 2.7c-.4.4-1 .4-1.4 0s-.4-1 0-1.4l2.7-2.7-2.7-2.7c-.4-.4-.4-1 0-1.4s1-.4 1.4 0l2.7 2.7 2.7-2.7c.4-.4 1-.4 1.4 0s.4 1 0 1.4l-2.7 2.7z"/>
        </g>
      </g>
    </svg>
  </span>
);

const TrueFalseEmbed = ({ node, updateAttributes, editor, getPos }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const exerciseIdRef = React.useRef(node.attrs.exerciseId);
  if (!exerciseIdRef.current) exerciseIdRef.current = crypto.randomUUID();
  const exerciseId = exerciseIdRef.current;
  useEffect(() => {
    if (!node.attrs.exerciseId) updateAttributes({ exerciseId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { myState, otherState, updateState, liveUpdateCount } = useExerciseSync(exerciseId);
  // Force ProseMirror to re-measure after expand/collapse so click coordinates stay accurate
  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => {
      try {
        editor.view.updateState(editor.view.state);
        // Mobile Safari/Chrome: re-syncing the view can leave the browser
        // thinking the (still logically-selected) editable region wants the
        // on-screen keyboard, even though the user only tapped this
        // exercise's own contentEditable=false UI. Blur ONLY if the outer
        // editor root itself still has focus (not an input inside the
        // exercise's own edit mode), so the keyboard does not pop on
        // expand/collapse.
        if (typeof document !== 'undefined' && document.activeElement === editor.view.dom) {
          editor.view.dom.blur();
        }
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror a REMOTE peer's expand/collapse live, without replaying stale
  // localStorage-hydrated data on every remount — see ExerciseSyncContext's
  // liveUpdateCount for why this can't just watch otherState.expanded alone.
  const lastLiveUpdateCountRef = React.useRef(liveUpdateCount);
  useEffect(() => {
    if (liveUpdateCount === lastLiveUpdateCountRef.current) return;
    lastLiveUpdateCountRef.current = liveUpdateCount;
    if (otherState.expanded !== undefined && otherState.expanded !== isExpanded) {
      setIsExpanded(otherState.expanded);
    }
  }, [liveUpdateCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const { statements = [], title = 'True/False Exercise', level = 'B1', alwaysShowAnswers = false } = node.attrs;

  const isStudent = (() => {
    try {
      // Check if we're in student mode by looking for student-specific attributes
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      
      // Check if we're in a student dashboard context
      const isStudentDashboard = typeof document !== 'undefined' && 
        (window.location.pathname.includes('/student-dashboard') || 
         window.location.pathname.includes('/student-profile'));
      
      return isStudentDashboard;
    } catch (_) { return false; }
  })();

  const isHomework = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-tab-type');
      if (fromRoot) return fromRoot === 'homework';
      const fromBody = typeof document !== 'undefined' ? document.body.getAttribute('data-novas-current-tab-type') : null;
      return fromBody === 'homework';
    } catch (_) { return false; }
  })();

  if (!statements || !Array.isArray(statements)) {
    return (
      <NodeViewWrapper>
        <div className="true-false-embed-error">
          <p>Error: Invalid True/False exercise data</p>
        </div>
      </NodeViewWrapper>
    );
  }

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    updateState({ expanded: next });
  };

  const handleClose = () => {
    setIsExpanded(false);
    updateState({ expanded: false });
  };

  // Allow placing cursor above/below the embed (like ABCD)
  const insertParagraphAt = (pos) => {
    if (!editor || typeof pos !== 'number') return;
    editor.chain().focus().insertContentAt(pos, '<p></p>').run();
  };

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getPos();
    insertParagraphAt(pos);
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getPos() + node.nodeSize;
    insertParagraphAt(pos);
  };

  // Drag handlers for visual feedback
  const handleDragStart = (e) => {
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(0.95)';
    e.target.style.outline = 'none';
    e.target.style.border = 'none';
    e.target.style.boxShadow = 'none';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    e.target.style.transform = '';
    e.target.style.outline = '';
    e.target.style.border = '';
    e.target.style.boxShadow = '';
    e.target.classList.remove('dragging');
  };

  // Only mark the outer wrapper / preview as a drag handle when the embed
  // is collapsed. With the expanded view also flagged as data-drag-handle,
  // ProseMirror treats clicks inside it (e.g. the Close button) as
  // node-selection / drag-start gestures. Same fix as ABCD/WordTranslation/
  // WordDefinition/FillInTheGaps/MultipleChoice embeds.
  const dragProps = isExpanded
    ? {}
    : { draggable: true, 'data-drag-handle': '', onDragStart: handleDragStart, onDragEnd: handleDragEnd };

  return (
    <NodeViewWrapper as="div" className="true-false-embed" contentEditable={false} {...dragProps}>
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div className="true-false-exercise-embed" {...dragProps}>
        {!isExpanded ? (
          <div className="embed-preview unified-exercise-preview" onClick={handleToggle} {...dragProps}>
          {TRUE_FALSE_ICON}
          <div className="unified-preview-main">
            <div className="unified-preview-title">{title}</div>
            <div className="unified-preview-meta">
              {statements.length} True/False statements · Level: {level}
            </div>
          </div>
          <div className="unified-preview-actions" onClick={e => e.stopPropagation()}>
            <button
              className="unified-icon-btn"
              title="Add to homework"
              style={{ display: (isHomework || isStudent) ? 'none' : 'inline-flex' }}
              onClick={() => {
                try {
                  window.dispatchEvent(new CustomEvent('novas:add-to-homework', {
                    detail: {
                      type: 'trueFalse',
                      attrs: { statements, title, level }
                    }
                  }));
                } catch (_) {}
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h4a.5.5 0 1 0 0-1h-4a.5.5 0 0 1-.5-.5V7.207l5-5 6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293z"/>
                <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1h-1a.5.5 0 0 0 0 1h1v1a.5.5 0 1 0 1 0v-1h1a.5.5 0 1 0 0-1h-1v-1a.5.5 0 0 0-.5-.5"/>
              </svg>
            </button>
            <button
              className="unified-icon-btn"
              title="Delete"
              style={{ display: isStudent ? 'none' : 'inline-flex' }}
              onClick={() => editor?.chain().focus().deleteRange({ from: getPos(), to: getPos() + node.nodeSize }).run()}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="embed-expanded">
          <TrueFalseExercise
            statements={statements}
            title={title}
            level={level}
            alwaysShowAnswers={alwaysShowAnswers}
            onClose={handleClose}
            updateAttributes={updateAttributes}
            isStudent={isStudent}
            myState={myState}
            otherState={otherState}
            updateState={updateState}
          />
        </div>
      )}
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default TrueFalseEmbed; 